import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { Macro } from "@redotech/effect-api-shared/rpc/macro";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { useLiveQuery } from "@tanstack/react-db";
import { useCallback, useState } from "react";
import {
  useAddTagSupportTicketAction,
  useCloseSupportTicketAction,
  useMarkInProgressSupportTicketAction,
  useReopenSupportTicketAction,
  useSnoozeSupportTicketAction,
} from "../actions/use-support-ticket-actions";
import { catchApiErrors } from "../db/error-handling";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";
import { useMacros } from "./use-macros";

export interface UseMacroStateResult {
  macros: readonly Macro[] | undefined;
  macroModalOpen: boolean;
  isApplyingMacro: boolean;
  handleMacroClick: () => void;
  handleApplyMacro: (macro: Macro) => Promise<void>;
  handleCloseMacroModal: () => void;
}

export function useMacroState(
  supportTicketId: SupportTicketId,
  onApplyContent: (content: string) => void,
): UseMacroStateResult {
  const [macroModalOpen, setMacroModalOpen] = useState(false);
  const [isApplyingMacro, setIsApplyingMacro] = useState(false);

  // Only fetch macros when the modal is opened (lazy loading)
  const { macros } = useMacros({ enabled: macroModalOpen });
  const effectApi = useRequiredContext(EffectApiClientContext);
  const collections = useGlobalSupportTicketCollections();
  const { data: allTags } = useLiveQuery(
    (q) => q.from({ tag: collections.tags }),
    [],
  );

  const closeAction = useCloseSupportTicketAction();
  const reopenAction = useReopenSupportTicketAction();
  const snoozeAction = useSnoozeSupportTicketAction();
  const markInProgressAction = useMarkInProgressSupportTicketAction();
  const addTagAction = useAddTagSupportTicketAction();

  const handleMacroClick = useCallback(() => {
    setMacroModalOpen(true);
  }, []);

  const handleCloseMacroModal = useCallback(() => {
    setMacroModalOpen(false);
  }, []);

  const handleApplyMacro = useCallback(
    async (macro: Macro) => {
      setIsApplyingMacro(true);
      try {
        const client = effectApi.getClient();
        const result = await effectApi.runPromise(
          catchApiErrors(
            client.macro.replaceMacroVariables({
              payload: { content: macro.htmlContent, supportTicketId },
            }),
          ),
        );
        onApplyContent(result.message);
      } catch {
        onApplyContent(macro.htmlContent);
      }

      if (macro.statusToSet) {
        const status = macro.statusToSet.toUpperCase();
        switch (status) {
          case "CLOSED":
            void closeAction.execute(supportTicketId);
            break;
          case "OPEN":
            void reopenAction.execute(supportTicketId);
            break;
          case "SNOOZED": {
            const duration = macro.snoozeDuration ?? "1h";
            //custom duration is not supported on macro automations
            void snoozeAction.execute(
              supportTicketId,
              duration === "custom" ? null : { duration },
            );
            break;
          }
          case "IN_PROGRESS":
            void markInProgressAction.execute(supportTicketId);
            break;
        }
      }

      if (macro.tagsToAdd?.length) {
        const tagIds = allTags
          .filter((tag) => macro.tagsToAdd!.includes(tag.name))
          .map((tag) => tag.id);
        if (tagIds.length) {
          void addTagAction.execute(supportTicketId, { tagIds });
        }
      }

      setIsApplyingMacro(false);
      setMacroModalOpen(false);
    },
    [
      effectApi,
      supportTicketId,
      onApplyContent,
      allTags,
      closeAction,
      reopenAction,
      snoozeAction,
      markInProgressAction,
      addTagAction,
    ],
  );

  return {
    macros,
    macroModalOpen,
    isApplyingMacro,
    handleMacroClick,
    handleApplyMacro,
    handleCloseMacroModal,
  };
}
